import { useTranslation } from 'react-i18next';

import type { PortalOverview } from '@/modules/portal/types/portalOverview';
import { portalDaysBadgeKey } from '@/modules/portal/utils/portalOverviewHelpers';
import { cn } from '@/shared/lib/cn';

export function PortalEmptyValue() {
  const { t } = useTranslation();
  return <span className="text-muted-foreground">{t('portal.overview.unavailable')}</span>;
}

export function PortalSubscriptionStatusBadge({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (!status) return <PortalEmptyValue />;

  const label = t(`subscriptions.status.${status}`, { defaultValue: status });
  const tone =
    status === 'active' || status === 'trialing'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : status === 'past_due' || status === 'pending_payment'
        ? 'bg-amber-500/15 text-amber-800 dark:text-amber-400'
        : 'bg-muted text-muted-foreground';

  return (
    <span
      className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-medium', tone)}
    >
      {label}
    </span>
  );
}

export function PortalSystemAccessBadge({
  status,
}: {
  status: PortalOverview['system_access_status'];
}) {
  const { t } = useTranslation();
  if (!status) return <PortalEmptyValue />;

  const label = t(`portal.overview.systemAccess.${status}`);
  const tone =
    status === 'active'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : status === 'limited'
        ? 'bg-amber-500/15 text-amber-800 dark:text-amber-400'
        : 'bg-red-500/15 text-red-700 dark:text-red-400';

  return (
    <span
      className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-medium', tone)}
    >
      {label}
    </span>
  );
}

export function PortalDaysRemainingDisplay({ overview }: { overview: PortalOverview }) {
  const { t } = useTranslation();
  const key = portalDaysBadgeKey(overview);
  const days = overview.days_remaining;

  if (!key) return <PortalEmptyValue />;

  if (key === 'unavailable') {
    return (
      <span className="text-lg text-muted-foreground">{t('trials.daysRemaining.unavailable')}</span>
    );
  }
  if (key === 'expired') {
    return (
      <span className="text-2xl font-bold tracking-tight text-red-600 dark:text-red-400">
        {t('trials.daysRemaining.expired')}
      </span>
    );
  }
  if (key === 'today') {
    return (
      <span className="text-2xl font-bold tracking-tight text-amber-600 dark:text-amber-400">
        {t('trials.daysRemaining.today')}
      </span>
    );
  }

  const isUrgent =
    days !== null &&
    days <= 7 &&
    (overview.subscription_status === 'trialing' || overview.subscription_status === 'active');

  return (
    <span
      className={cn(
        'text-3xl font-bold tracking-tight',
        isUrgent ? 'text-amber-600 dark:text-amber-400' : 'text-foreground',
      )}
    >
      {t('trials.daysRemaining.left', { count: days ?? 0 })}
    </span>
  );
}

export function PortalTenantStatusBadge({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (!status) return <PortalEmptyValue />;

  const label = t(`portal.companyProfile.tenantStatus.${status}`, { defaultValue: status });
  const tone =
    status === 'active'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : status === 'pending'
        ? 'bg-amber-500/15 text-amber-800 dark:text-amber-400'
        : 'bg-muted text-muted-foreground';

  return (
    <span
      className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-medium', tone)}
    >
      {label}
    </span>
  );
}

export function PortalCustomerRoleBadge({ role }: { role: string | null }) {
  const { t } = useTranslation();
  if (!role) return <PortalEmptyValue />;

  const label = t(`portal.companyProfile.roles.${role}`, { defaultValue: role });
  return (
    <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
      {label}
    </span>
  );
}

export function PortalAutoRenewBadge({ autoRenew }: { autoRenew: boolean | null }) {
  const { t } = useTranslation();
  if (autoRenew === null) return <PortalEmptyValue />;

  const label = autoRenew
    ? t('portal.subscription.autoRenew.on')
    : t('portal.subscription.autoRenew.off');
  const tone = autoRenew
    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    : 'bg-muted text-muted-foreground';

  return (
    <span
      className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-medium', tone)}
    >
      {label}
    </span>
  );
}
